import { useEffect, useState } from "react";
import { isInsideTeams } from "./useOnline";
import {
  awaitPlatformAuthHandoff,
  confirmBackendSession,
  confirmCookieBackendSession,
  fetchAuthKitSession,
  isEmbeddedInHub,
  isNationalDatacareHost,
  isPlatformLaunch,
  readPlatformSession,
} from "@/lib/platformAuth";

export type BrowserAuthState = "checking" | "authenticated" | "unauthenticated";

/**
 * For browser (non-Teams) sessions, verifies the user is signed in via
 * Auth-Kit cookie SSO or Azure Easy Auth.
 *
 * - Inside Teams: immediately returns "authenticated" — Teams SSO (useTeamsAuth)
 *   handles identity; no Easy Auth session check is needed.
 * - On ka.nationaldatacare.com (shared Auth-Kit cookie): first asks `/api/me`
 *   with credentials (Node unseals `wos-session`). Then Identity session-token
 *   if needed. The SPA must not require a JS-readable marker — that cookie is
 *   often HttpOnly or host-only on Hub, so Hub iframes would always look signed-out.
 * - Platform Hub new-tab launch (fallback): waits for postMessage handoff.
 * - Outside Teams: calls /.auth/me.
 *   Unauthenticated → "unauthenticated" so the app can show a sign-in page
 *   (does not auto-redirect to Microsoft login).
 *   /.auth/me returns 404 (Easy Auth not configured, e.g. local dev) →
 *   "authenticated" so local Vite workflows still work.
 *
 * Fail-open only when Easy Auth is absent (404).
 *
 * This approach keeps Azure Easy Auth set to "Allow unauthenticated requests",
 * which lets the Teams iframe load without being blocked by a 302 redirect.
 * The browser auth wall is enforced here in the client instead.
 */
export function useBrowserAuth(): BrowserAuthState {
  const [state, setState] = useState<BrowserAuthState>(() =>
    isInsideTeams() ? "authenticated" : "checking",
  );

  useEffect(() => {
    if (isInsideTeams()) return;

    let cancelled = false;

    async function acceptPlatformSession() {
      const existing = readPlatformSession();
      if (existing?.accessToken && (await confirmBackendSession(existing))) {
        return true;
      }
      if (isNationalDatacareHost() && (await confirmCookieBackendSession())) {
        return true;
      }
      if (cancelled) return false;
      const cookieSession = await fetchAuthKitSession();
      if (cancelled) return false;
      if (cookieSession?.accessToken && (await confirmBackendSession(cookieSession))) {
        return true;
      }
      if (isPlatformLaunch()) {
        const platform = await awaitPlatformAuthHandoff(
          isEmbeddedInHub() ? 1500 : 8000,
        );
        if (cancelled) return false;
        if (platform?.accessToken && (await confirmBackendSession(platform))) {
          return true;
        }
      }
      return false;
    }

    async function check() {
      if (await acceptPlatformSession()) {
        if (!cancelled) setState("authenticated");
        return;
      }
      if (cancelled) return;

      // Hub / branded KA host: Auth-Kit login only. Do not gate on Easy Auth AAD.
      if (isNationalDatacareHost()) {
        setState("unauthenticated");
        return;
      }

      try {
        const res = await fetch("/.auth/me");
        if (cancelled) return;

        if (res.status === 404) {
          setState("authenticated");
          return;
        }

        if (res.status === 401 || !res.ok) {
          setState("unauthenticated");
          return;
        }

        const data = (await res.json()) as unknown;
        if (cancelled) return;

        if (Array.isArray(data) && data.length > 0) {
          setState("authenticated");
        } else {
          setState("unauthenticated");
        }
      } catch {
        if (!cancelled) setState("unauthenticated");
      }
    }

    void check();
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
